import {
  execute,
  FilterConditions,
  INSERT,
  SELECT,
  UPDATE,
} from "@/utils/database_module";
import { logDebug, logError } from "@/utils/helpers";
import {
  CarOrder,
  Conflict,
  ConflictHistory,
  ConflictStatus,
  Devis,
  Order,
  OrderHistory,
  OrderStatus,
  Car,
  SpecialOrderQuote,
  User,
  UserSession,
  Versement,
} from "@/utils/types";
import { CarOrdersStatuses } from "@/utils/utilLists";
import { get } from "http";
import moment from "moment";

type OrderData = Omit<
  Order,
  "id" | "date" | "modified_date" | "name" | "car" | "user" | "history"
>;

const carsOrders = {
  getAll: async (filter: {
    selectedStatus?: any;
    startDate?: string;
    endDate?: string;
    limit?: number;
    order?: string;
    user_id?: number;
    currentPage?: number;
  }) => {
    let {
      selectedStatus,
      startDate,
      endDate,
      user_id,
      limit = 1000000,
      currentPage = 1,
      order,
    } = filter;

    // Initialize query conditions
    let conditions: FilterConditions = [
      { column: "is_deleted", operator: "=", value: 0 },
    ];

    // Filter by user ID if provided
    if (user_id)
      conditions.push({ column: "user_id", operator: "=", value: user_id });

    // Filter by selected status if not equal to 0
    if (selectedStatus && parseInt(selectedStatus) !== 0)
      conditions.push({
        column: "status",
        operator: "=",
        value: parseInt(selectedStatus),
      });

    logDebug("orders getall", conditions);

    // Fetch orders from DB with conditions and optional ordering
    const orders = (await SELECT("carsOrders", {
      conditions,
      order: order || "modified_date DESC",
      limit: Number.isNaN(parseInt(`${limit * currentPage}`))
        ? limit
        : parseInt(`${limit * currentPage}`),
    })) as CarOrder[];

    // Filter orders by startDate and endDate if provided
    const filteredOrders = orders.filter((order) => {
      const orderDate = moment(order.date).format("YYYY-MM-DD");

      // If start date is defined and order is before it, exclude
      if (
        startDate &&
        !moment(orderDate).isSameOrAfter(moment(startDate).format("YYYY-MM-DD"))
      )
        return false;

      // If end date is defined and order is after it, exclude
      if (
        endDate &&
        !moment(orderDate).isSameOrBefore(moment(endDate).format("YYYY-MM-DD"))
      )
        return false;

      return true;
    });

    // If no matching orders, return empty array early
    if (filteredOrders.length === 0) return [];

    // Collect user IDs from filtered orders
    const userIDS = filteredOrders.map((order) => order.user_id);

    // Fetch user data for all users associated with filtered orders
    const userQuery = `
    SELECT id, first_name, last_name, email, phone_numbers, profile_pic, token, adress
    FROM users
    WHERE role = 'user' AND id IN (${userIDS.join(", ")})
    ORDER BY id DESC
  `;
    const users = (await execute(userQuery)) as User[];

    // Parse phone_numbers if needed
    for (const user of users) {
      user.phone_numbers =
        typeof user.phone_numbers === "string"
          ? JSON.parse(user.phone_numbers)
          : user.phone_numbers;
    }

    // Collect car IDs from filtered orders
    const carIDs = filteredOrders.map((order) => order.car_id);

    // Fetch car data only if carIDs array is not empty
    const cars =
      carIDs.length > 0
        ? ((await execute(
            `SELECT * FROM cars WHERE id IN (${carIDs.join(", ")})`
          )) as Car[])
        : [];

    // Parse car names if they are stored as strings
    for (const car of cars) {
      car.name = typeof car.name === "string" ? JSON.parse(car.name) : car.name;
    }

    // Enrich orders with their related user, car, and status info
    for (const order of filteredOrders) {
      order.user = users.find((user) => user.id === order.user_id);
      order.car = cars.find((car) => car.id === order.car_id);

      // Attach human-readable status object
      // @ts-ignore
      order.status = CarOrdersStatuses.find(
        (status) => status.id === order.status
      );

      // Parse car fields if needed
      if (order.car) {
        order.car.images =
          typeof order.car.images === "string"
            ? JSON.parse(order.car.images)
            : order.car.images;

        order.car.name =
          typeof order.car.name === "string"
            ? JSON.parse(order.car.name)
            : order.car.name;
      }
    }

    // Return the fully enriched and filtered orders
    return filteredOrders;
  },

  getOne: async (id: number) => {
    const [order] = (await SELECT("carsOrders", {
      conditions: [{ column: "id", operator: "=", value: id }],
    })) as CarOrder[];
    if (!order) throw new Error("Order not found");

    [order.user] = (await SELECT("users", {
      conditions: [
        { column: "id", operator: "=", value: order.user_id },
      ] as FilterConditions,
    })) as User[];

    order.status = CarOrdersStatuses.find(
      (status) => status.id == order.status
    );

    [order.car] = (await SELECT("cars", {
      conditions: [{ column: "id", operator: "=", value: order.car_id }],
    })) as Car[];

    return order;
  },

  update: async (id: number, data: { order: Partial<CarOrder> }) => {
    const { order } = data;
    return await UPDATE("carsOrders", order, [
      { column: "id", operator: "=", value: id },
    ]);
  },

  insert: async (data: CarOrder) => {
    await INSERT("carsOrders", { ...data, status: 1 });
  },

  delete: async (id: number) => {
    console.log("delete");
    return await UPDATE("carsOrders", { is_deleted: 1 }, [
      { column: "id", operator: "=", value: id },
    ]);
  },
};

const apiError = (statusCode: number, message: string) => {
  console.log(`---------------- [API Error ] ------------------`);
  logError(message);
  return Response.json({ error: message, code: statusCode });
};

export default carsOrders;
